import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Check,
  ChevronDown,
  ChevronUp,
  Download,
  EyeOff,
  Paperclip,
  Pencil,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { JournalAttachments } from '@/components/journal/JournalAttachments';
import { JournalFormattedView } from '@/components/journal/JournalFormattedView';
import {
  useJournalAmendments,
  type JournalEntryWithAuthor,
} from '@/hooks/useJournal';
import {
  formatCourseDate,
  formatCourseDateParts,
  formatCourseDateTime,
  formatCourseTime,
  parseCourseCivil,
} from '@/lib/courseDateFormat';
import { journalEntryListPreview, journalReadHtml } from '@/lib/journal/journalReadContent';
import { appointmentCourseStepPath } from '@/lib/treatmentCoursePatientParams';
import { cn } from '@/utils/cn';
import {
  journalDisplayAppointment,
  journalEntryStatus,
  journalKindLabel,
  type JournalLinkedAppointment,
} from '@/utils/journalHistoryFilters';

const ICON = 1.75;

function statusChipClass(status: 'draft' | 'finalized' | 'discarded'): string {
  switch (status) {
    case 'finalized':
      return 'chip chip-final';
    case 'draft':
      return 'chip chip-draft';
    case 'discarded':
      return 'chip chip-neutral';
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

export function journalTabPractitionerName(
  entry: JournalEntryWithAuthor,
  linked: JournalLinkedAppointment | null,
  members: ReadonlyArray<{ user_id: string; name: string }>,
): string | null {
  const fromProfile =
    members.find((row) => row.user_id === entry.clinician_id)?.name ||
    members.find((row) => row.user_id === entry.created_by)?.name;
  const name = fromProfile || linked?.clinician_name || entry.author_name;
  return name?.trim() || null;
}

interface JournalTabNoteProps {
  entry: JournalEntryWithAuthor;
  patientId: string;
  clinicId: string;
  appointment?: JournalLinkedAppointment | null;
  courseName?: string | null;
  courseId?: string | null;
  stepId?: string | null;
  stepIndex?: number | null;
  stepCount?: number | null;
  practitionerName: string | null;
  open: boolean;
  onToggle: () => void;
  attachmentCount: number;
  amendmentCount: number;
  members: ReadonlyArray<{ user_id: string; name: string }>;
  currentUserId?: string | null;
  canCreate: boolean;
  canAmend: boolean;
  exporting: boolean;
  onContinue: () => void;
  onCorrect: () => void;
  onExportNote: () => void;
}

export function JournalTabNote({
  entry,
  patientId,
  clinicId,
  appointment,
  courseName,
  courseId,
  stepId,
  stepIndex,
  stepCount,
  practitionerName,
  members,
  currentUserId,
  open,
  onToggle,
  attachmentCount,
  amendmentCount,
  canCreate,
  canAmend,
  exporting,
  onContinue,
  onCorrect,
  onExportNote,
}: JournalTabNoteProps) {
  const { t } = useTranslation();
  const status = journalEntryStatus(entry);
  const linked = journalDisplayAppointment(entry, appointment ?? null);
  const kind = journalKindLabel({
    entryType: entry.entry_type,
    serviceName: linked?.service_name,
    typeLabel: (type) => t(`journal.type_${type}`),
  });
  const clockSource = linked?.starts_at ?? entry.encounter_at;
  const civil = clockSource ? null : parseCourseCivil(entry.entry_date);
  const dateLabel = clockSource
    ? formatCourseDate(clockSource, 'da')
    : civil
      ? formatCourseDateParts(civil, 'da')
      : '';
  const timeLabel = formatCourseTime(clockSource ?? entry.created_at);
  const preview = journalEntryListPreview(entry.content_html, entry.content_md);
  const courseHref =
    courseId && stepId
      ? appointmentCourseStepPath({ patientId, courseId, stepId })
      : null;
  const courseLabel =
    courseName && stepIndex != null && stepCount != null
      ? t('journal.write_band_course', { name: courseName, n: stepIndex, m: stepCount })
      : null;
  const headingId = `journal-note-h-${entry.id}`;
  const bodyId = `journal-note-b-${entry.id}`;
  const showContinue = status === 'draft' && canCreate;
  const showCorrect = status === 'finalized' && canAmend;
  const hiddenHeadings = useMemo(
    () => (open && status === 'draft' ? journalReadHtml(entry.content_html, entry.content_md).hiddenHeadings : []),
    [entry.content_html, entry.content_md, open, status],
  );
  const footerWhen = formatCourseDateTime(
    status === 'finalized' ? entry.finalized_at ?? entry.updated_at : entry.updated_at ?? entry.created_at,
    'da',
  );

  return (
    <li className={cn('jf-it', open && 'open')} data-journal-nav-id={entry.id}>
      <div className="jf-h">
        <div className="dt">
          {dateLabel}
          {timeLabel ? <small>{t('journal.clock', { time: timeLabel })}</small> : null}
        </div>
        <div className="mid">
          <div className="tt">
            <h3>
              <button
                type="button"
                id={headingId}
                className="jf-tg"
                aria-expanded={open}
                aria-controls={bodyId}
                onClick={onToggle}
              >
                {entry.title || t('journal.untitled')}
              </button>
            </h3>
            <span className={statusChipClass(status)}>
              {status === 'finalized' ? (
                <Check className="h-3.5 w-3.5" strokeWidth={ICON} aria-hidden />
              ) : status === 'draft' ? (
                <span className="dot" aria-hidden />
              ) : null}
              {status === 'discarded'
                ? t('journal.discarded_chip')
                : t(`journal.filter_status_${status}`)}
            </span>
          </div>
          {(kind || practitionerName || courseLabel) && (
            <p className="ss">
              {[kind, practitionerName].filter(Boolean).join(' · ')}
              {courseLabel ? (
                <>
                  {kind || practitionerName ? ' · ' : null}
                  {courseHref ? (
                    <Link to={courseHref}>{courseLabel}</Link>
                  ) : (
                    <span>{courseLabel}</span>
                  )}
                </>
              ) : null}
            </p>
          )}
          {!open && preview ? <p className="pv">{preview}</p> : null}
        </div>
        <div className="rr">
          {attachmentCount > 0 ? (
            <span className="ind">
              <Paperclip className="h-4 w-4" strokeWidth={ICON} aria-hidden />
              {attachmentCount}
              <span className="sr-only">{t('journal.attachments_sr')}</span>
            </span>
          ) : null}
          {amendmentCount > 0 ? (
            <span className="ind">{t('journal.amendment_count', { count: amendmentCount })}</span>
          ) : null}
          <span className="chev" aria-hidden>
            {open ? (
              <ChevronUp className="h-4 w-4" strokeWidth={ICON} />
            ) : (
              <ChevronDown className="h-4 w-4" strokeWidth={ICON} />
            )}
          </span>
        </div>
      </div>

      <div
        id={bodyId}
        className="jf-b"
        role="region"
        aria-labelledby={headingId}
        hidden={!open}
      >
        {open ? (
          <>
            <JournalFormattedView
              variant="read"
              html={entry.content_html}
              text={entry.content_md}
            />
            {status === 'draft' && hiddenHeadings.length > 0 ? (
              <p className="jf-hid">
                <EyeOff className="h-4 w-4" strokeWidth={ICON} aria-hidden />
                {t('journal.hidden_template_sections', { list: hiddenHeadings.join(', ') })}
              </p>
            ) : null}
            <JournalAttachments
              clinicId={clinicId}
              patientId={patientId}
              entryId={entry.id}
              canEdit={false}
              variant="read"
            />
            <JournalTabAddenda
              clinicId={clinicId}
              patientId={patientId}
              entryId={entry.id}
              members={members}
              currentUserId={currentUserId}
            />
            <div className="jf-f">
              {showContinue ? (
                <Button type="button" variant="secondary" onClick={onContinue}>
                  <Pencil className="h-4 w-4" strokeWidth={ICON} />
                  {t('treatment_courses.continue_journal')}
                </Button>
              ) : null}
              {showCorrect ? (
                <Button type="button" variant="secondary" onClick={onCorrect}>
                  <Pencil className="h-4 w-4" strokeWidth={ICON} />
                  {t('journal.add_correction')}
                </Button>
              ) : null}
              <Button
                type="button"
                variant="secondary"
                onClick={onExportNote}
                disabled={exporting}
              >
                <Download className="h-4 w-4" strokeWidth={ICON} />
                {t('journal.export_note')}
              </Button>
              {footerWhen ? (
                <span className="st">
                  {status === 'finalized'
                    ? t('journal.finalized_at', { date: footerWhen })
                    : t('journal.saved_at_short', { date: footerWhen })}
                </span>
              ) : null}
            </div>
          </>
        ) : null}
      </div>
    </li>
  );
}

function JournalTabAddenda({
  clinicId,
  patientId,
  entryId,
  members,
  currentUserId,
}: {
  clinicId: string;
  patientId: string;
  entryId: string;
  members: ReadonlyArray<{ user_id: string; name: string }>;
  currentUserId?: string | null;
}) {
  const { t } = useTranslation();
  const amendmentsQuery = useJournalAmendments(entryId);
  const rows = (amendmentsQuery.data ?? []).filter((row) => {
    if (row.discarded_at) return false;
    if (row.finalized_at) return true;
    return Boolean(currentUserId && row.created_by === currentUserId);
  });
  if (rows.length === 0) return null;

  return (
    <>
      {rows.map((row) => {
        const when = formatCourseDateTime(row.finalized_at ?? row.created_at, 'da');
        const author =
          members.find((member) => member.user_id === row.created_by)?.name ||
          row.author_name_snapshot;
        const details = [when, author].filter(Boolean).join(' · ');
        return (
          <div key={row.id} className="jf-add">
            <div className="k">
              <b>{t('journal.amendment_label')}</b>
              {details ? ` · ${details}` : null}
              {!row.finalized_at ? (
                <>
                  {' · '}
                  <span className="chip chip-draft">{t('journal.filter_status_draft')}</span>
                </>
              ) : null}
            </div>
            <JournalFormattedView
              variant="read"
              html={row.content_html}
              text={row.content_md}
            />
            <JournalAttachments
              clinicId={clinicId}
              patientId={patientId}
              amendmentId={row.id}
              canEdit={false}
              variant="read"
            />
          </div>
        );
      })}
    </>
  );
}
